import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
// Store
import { IRootState } from '../../../redux';
// Components
import {
    Image,
    Button,
    ScrollView,
    // RoundButton,
    // Input,
    Dropdown,
    FixedLoader,
    PopupContent,
    Popup,
    PopupButtons,
} from '../..';
// Assets
import images from '../../../assets/images/images';
// Styles
import styles from './contactPointSearcher.module.scss';
// Config
import { ConfigContext } from '../../../providers';
import { staticConfig } from '../../../config/static';
// Enums
import {
    ContactPointViewItemType,
    MailboxAccessType,
    MailboxOwnerType,
    OrganizationCategoryAction,
    Targets,
    sortByDa,
} from '../../../enums';
// Interfaces
import { IKeyValuePair, IResponseCollection } from '../../../interfaces/general.interfaces';
import { IOrganisationIdent } from '../../../interfaces/organisations.interfaces';
import {
    IContactGroup,
    IContactPoint,
    IContactPointViewItem,
    IOrganisationContactGroupsWithContactPointsResponse,
} from '../../../interfaces/contactpoint.interfaces';
// Providers
import {
    getContactGroupsAndContactPointsByOrganisationGroupId,
    getContactGroupsAndContactPointsByOrganisationId,
    getOrganisationsBySearchValue,
} from '../../../services';
// Helpers
import { IAccess, ITrustee } from '../../../interfaces/mailbox.interfaces';
import { ContactPointViewLine } from './ContactPointViewLine';
import dbounce from 'debounce';
import { sortDa } from '../../../helpers/sortda';
import { ISelectionAction } from '../../MenuBar/MenuBarDropdownList/MenuBarDropdownList';

interface IContactPointSearcherProps {
    onClose?: () => void;
    onContactPointSelected: (contactPoint: IContactPointViewItem) => void;
    onTrusteeSelected?: (trustee: ITrustee) => void;
    trusteeList?: ITrustee[];
    selectedTrustee?: any;
    selectedUserAccess?: IAccess;
}

export const ContactPointSearcher = ({
    onClose,
    onContactPointSelected,
    onTrusteeSelected,
    trusteeList,
    selectedTrustee,
    selectedUserAccess,
}: IContactPointSearcherProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [skipCount, setSkipCount] = useState(0);
    const [category, setCategory] = useState('');
    const [organisations, setOrganisations] = useState<IOrganisationIdent[]>([]);
    const [fwToContactPointViews, setFwToContactPointViews] = useState<IContactPointViewItem[]>([]);
    const [viewElements, setViewElements] = useState<JSX.Element[]>([]);
    const [isCategorySelected, setIsCategorySelected] = useState(false);

    const { formatMessage } = useIntl();

    const config = useContext(ConfigContext);

    const { mediaSize, mailbox } = useSelector(({ appReadouts, mailbox }: IRootState) => ({
        mediaSize: appReadouts.mediaSize,
        mailbox: mailbox.mailbox,
    }));

    const getOrganisations = useCallback(() => {
        if ((searchValue === undefined || searchValue === '') && (category === undefined || category === '')) {
            return;
        }

        setIsLoading(true);

        const request: any = {
            name: searchValue ? '*' + searchValue + '*' : category ? '*' : '',
            // Changes for Removing S/I
            // skipCount: skipCount,
            // takeCount: 1000,
            // sortFields: ['name'],
            target: mailbox.ownerType == MailboxOwnerType.ORGANISATION ? Targets.COMPANY : Targets.CITIZEN,
        };

        if (category && category !== '') {
            const cat: IKeyValuePair = { authorityType: category };
            request.searchParameters = cat;
        }

        getOrganisationsBySearchValue(request).then((organisations: IResponseCollection<IOrganisationIdent>) => {
            if (organisations?.items?.length === 0 && skipCount > 0) {
                setSkipCount(skipCount - 1);
            } else {
                setOrganisations(organisations.items);
            }
            setIsLoading(false);
        });
    }, [category, searchValue, skipCount]);

    const getTarget = useCallback((): Targets => {
        switch (mailbox.ownerType) {
            case MailboxOwnerType.ORGANISATION:
                return Targets.COMPANY;

            case MailboxOwnerType.CITIZEN:
            default:
                return Targets.CITIZEN;
        }
    }, [mailbox.ownerType]);

    const typeAheadSearch = useCallback(
        dbounce((q: string) => {
            skipCount !== 0 && setSkipCount(0);
            setSearchValue(q.trim());
        }, 700),
        []
    );

    const handleOnChange = (value: string): string => {
        setInputValue(value);
        return '';
    };

    const sortByDanish = (array: any[]) => {
        array.sort(function (a, b) {
            return sortDa(a.name, b.name, sortByDa.AA_LAST);
        });
    };

    const sortContactPointGroup = (contacts: IOrganisationContactGroupsWithContactPointsResponse) => {
        sortByDanish(contacts.contactPoints.items);
        sortByDanish(contacts.contactGroups.items);
    };

    useEffect(() => {
        if (inputValue.length >= 3) typeAheadSearch(inputValue);
        if (inputValue === '') {
            setSearchValue('');
        }
    }, [inputValue]);

    const handleKeyDown = (event: React.KeyboardEvent): void => {
        if (event.key === 'Enter') {
            const input = event.target as HTMLInputElement;
            setSkipCount(0);
            setSearchValue(input.value.trim());
        }
    };

    useEffect(() => {
        const fwToCpViews = organisations.map<IContactPointViewItem>((organisationIdent: IOrganisationIdent) => {
            return {
                id: organisationIdent.id,
                version: 0,
                name: organisationIdent.name,
                fullName: null,
                description: null,
                type: ContactPointViewItemType.Organisation,
                active: true,
                contactPoint: null,
                children: [],
                showChildren: false,
                parentId: null,
                organisationId: organisationIdent.id,
                level: 0,
                cvrNumber: organisationIdent.cvrNumber,
            };
        });
        setFwToContactPointViews(fwToCpViews);
        sortByDanish(fwToCpViews);
    }, [organisations]);

    useEffect(() => {
        const getOrganisationChildren = async (
            organisationId: string,
            cvrNumber: string,
            organisationName: string
        ): Promise<IContactPointViewItem[]> => {
            const orgContGroupContacts = await getContactGroupsAndContactPointsByOrganisationId(
                organisationId,
                getTarget(),
                organisationName
            );

            sortContactPointGroup(orgContGroupContacts);

            const cpViews = orgContGroupContacts.contactPoints.items.map<IContactPointViewItem>(
                (contactPoint: IContactPoint) => {
                    return {
                        id: contactPoint.id,
                        name: contactPoint.name,
                        fullName: contactPoint.fullName,
                        description: contactPoint.description,
                        organisationId: contactPoint.organisationId,
                        contactPoint: contactPoint,
                        version: contactPoint.version,
                        type: ContactPointViewItemType.ContactPoint,
                        active: contactPoint.active,
                        level: 1,
                        parentId: contactPoint.organisationId,
                        showChildren: false,
                        children: [],
                        cvrNumber: cvrNumber,
                    };
                }
            );

            return cpViews.concat(
                orgContGroupContacts.contactGroups.items.map<IContactPointViewItem>((contactGroup: IContactGroup) => {
                    return {
                        id: contactGroup.id,
                        name: contactGroup.name,
                        fullName: contactGroup.fullName,
                        description: contactGroup.description,
                        organisationId: contactGroup.organisationId,
                        contactPoint: null,
                        version: contactGroup.version,
                        type: ContactPointViewItemType.ContactGroup,
                        active: true,
                        level: 1,
                        parentId: contactGroup.parentId,
                        showChildren: false,
                        children: [],
                        cvrNumber: cvrNumber,
                    };
                })
            );
        };

        const getContactGroupChildren = async (
            organisationId: string,
            cvrNumber: string,
            contactGroupId: string,
            level: number,
            contactGroupName: string,
            fullName: string
        ): Promise<IContactPointViewItem[]> => {
            const contGroupContacts = await getContactGroupsAndContactPointsByOrganisationGroupId(
                organisationId,
                contactGroupId,
                fullName,
                getTarget()
            );
            localStorage.setItem(
                'contactPointGroupInfo',
                JSON.stringify({ contactGroup: contactGroupName, label: null, contactPointId: null })
            );

            sortContactPointGroup(contGroupContacts);

            const cpViews = contGroupContacts.contactPoints.items.map<IContactPointViewItem>(
                (contactPoint: IContactPoint) => {
                    return {
                        id: contactPoint.id,
                        name: contactPoint.name,
                        fullName: contactPoint.fullName,
                        description: contactPoint.description,
                        organisationId: contactPoint.organisationId,
                        contactPoint: contactPoint,
                        version: contactPoint.version,
                        type: ContactPointViewItemType.ContactPoint,
                        active: contactPoint.active,
                        level: level + 1,
                        parentId: contactGroupId,
                        showChildren: false,
                        children: [],
                        cvrNumber: cvrNumber,
                    };
                }
            );

            return cpViews.concat(
                contGroupContacts.contactGroups.items.map<IContactPointViewItem>((contactGroup: IContactGroup) => {
                    return {
                        id: contactGroup.id,
                        name: contactGroup.name,
                        fullName: contactGroup.fullName,
                        description: contactGroup.description,
                        organisationId: contactGroup.organisationId,
                        contactPoint: null,
                        version: contactGroup.version,
                        type: ContactPointViewItemType.ContactGroup,
                        active: true,
                        level: level + 1,
                        parentId: contactGroup.parentId,
                        showChildren: false,
                        children: [],
                        cvrNumber: cvrNumber,
                    };
                })
            );
        };

        const updateView = (viewToUpdate: IContactPointViewItem, viewList: Array<IContactPointViewItem>): void => {
            for (let view of viewList) {
                if (view.id === viewToUpdate.id) {
                    view = viewToUpdate;
                    break;
                }

                if (view.children.length > 0) {
                    updateView(viewToUpdate, view.children);
                }
            }
        };

        /**
         * 
         * @param fwToCpView 
         * @param event 
         * @returns Static message if inacive contact points.
         * FREM-3290 - Keeping commented if code is required in future.
         * We can remove after final requirenmt is confirmend.         
            function getStaticView(view: IContactPointViewItem): IContactPointViewItem[] {
                return [
                    {
                        id: view.id + '-1',
                        name:
                            view.level === 0
                                ? formatMessage({ id: 'ORGANISATION_NO_CHILDREN_TEXT' })
                                : formatMessage({ id: 'CONTACTGROUP_NO_CHILDREN_TEXT' }),
                        description: null,
                        organisationId: null,
                        cvrNumber: null,
                        contactPoint: null,
                        version: 0,
                        type: ContactPointViewItemType.Static,
                        active: false,
                        level: view.level + 1,
                        parentId: view.id,
                        showChildren: false,
                        children: [],
                    },
                ];
            }
        */

        const toggleParentClick = async (
            fwToCpView: IContactPointViewItem,
            event: React.SyntheticEvent
        ): Promise<void> => {
            if (event) {
                event.stopPropagation();
            }

            if (fwToCpView.type === ContactPointViewItemType.ContactPoint) {
                return;
            }

            const newFwToCpViews = [...fwToContactPointViews];

            fwToCpView.showChildren = !fwToCpView.showChildren;

            const contactPointIsOrganization = fwToCpView.children.length === 0 && fwToCpView.level < 1;
            const contactPointIsGroup = fwToCpView.children.length === 0 && fwToCpView.level > 0;

            if (contactPointIsOrganization) {
                const fwToCpViews = await getOrganisationChildren(fwToCpView.id, fwToCpView.cvrNumber, fwToCpView.name);

                if (fwToCpViews.length === 0 && fwToCpView.showChildren) {
                    onContactPointSelected(fwToCpView);
                    // fwToCpView.children = getStaticView(fwToCpView);
                } else {
                    fwToCpView.children = fwToCpViews;
                }

                updateView(fwToCpView, newFwToCpViews);
                setFwToContactPointViews(newFwToCpViews);
            } else if (contactPointIsGroup) {
                const fwToCpViews = await getContactGroupChildren(
                    fwToCpView.organisationId,
                    fwToCpView.cvrNumber,
                    fwToCpView.id,
                    fwToCpView.level,
                    fwToCpView.name,
                    fwToCpView.fullName
                );

                if (fwToCpViews.length === 0 && fwToCpView.showChildren) {
                    onContactPointSelected(fwToCpView);
                    // fwToCpView.children = getStaticView(fwToCpView);
                } else {
                    fwToCpView.children = fwToCpViews;
                }

                updateView(fwToCpView, newFwToCpViews);
                setFwToContactPointViews(newFwToCpViews);
            } else {
                // ContactPoint is leaf node
                updateView(fwToCpView, newFwToCpViews);
                setFwToContactPointViews(newFwToCpViews);
            }
        };

        const onViewLineClick = (fwToCpView: IContactPointViewItem, event: React.SyntheticEvent): void => {
            if (fwToCpView.type === ContactPointViewItemType.Static || !fwToCpView.active) {
                return;
            }

            if (fwToCpView.type === ContactPointViewItemType.ContactPoint) {
                onContactPointSelected(fwToCpView);
            } else {
                toggleParentClick(fwToCpView, event);
            }
        };

        // Recursive reducer to extract whole ContactPoint nested tree
        const getContactPointViewLineElements = (
            contactPointViews: IContactPointViewItem[],
            level: number
        ): JSX.Element[] =>
            contactPointViews.reduce((viewList, viewItem) => {
                const showChildren = viewItem?.showChildren && viewItem?.children?.length > 0;
                return !showChildren
                    ? [
                          ...viewList,
                          <ContactPointViewLine
                              key={'cveKey' + level + '_' + viewItem.id}
                              view={viewItem}
                              level={level}
                              onClick={onViewLineClick}
                              serachInputValue={inputValue}
                          />,
                      ]
                    : [
                          ...viewList,
                          <ContactPointViewLine
                              key={'cveKey' + level + '_' + viewItem.id}
                              view={viewItem}
                              level={level}
                              onClick={onViewLineClick}
                              serachInputValue={inputValue}
                          />,
                          getContactPointViewLineElements(viewItem.children, level + 1),
                      ];
            }, []);

        const viewElementList = getContactPointViewLineElements(fwToContactPointViews, 0);
        setViewElements(viewElementList);
    }, [fwToContactPointViews, onContactPointSelected, getTarget, formatMessage]);

    const handleItemSelected = (action: string): void => {
        setSkipCount(0);
        setSearchValue('');
        setInputValue('');
        setCategory(action);
        setIsCategorySelected(true);
    };

    // const pageLeft = (): void => {
    //     if (skipCount > 0) {
    //         setSkipCount(skipCount - 1);
    //     }
    // };

    // const pageRight = (): void => {
    //     if (organisations.length === 5) {
    //         setSkipCount(skipCount + 1);
    //     }
    // };

    const searchClick = (event: React.MouseEvent): void => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
            setSkipCount(0);
            setSearchValue(inputValue.trim());
        }
    };

    useEffect(() => {
        getOrganisations();
    }, [skipCount, getOrganisations]);

    const showEmptyText = viewElements.length === 0 && skipCount === 0;

    const trusteeSelector =
        trusteeList?.length > 0 ? (
            <div className="w-100 mb-2">
                <Dropdown
                    className="w-100"
                    list={trusteeList.map(({ id, name, idType }) => ({ key: name, value: id, idType: idType }))}
                    onSelect={({ key, value, idType }: { key: string; value: string; idType: string }): void =>
                        onTrusteeSelected({ id: value, name: key, idType: idType, fullName: key })
                    }
                    placeholder="TRUSTED_RECIPIENT"
                    selectedTrusteeFromList={selectedTrustee}
                />
            </div>
        ) : null;

    const inputElement = useRef(null);
    useEffect(() => {
        if (inputElement.current) {
            inputElement.current.focus();
        }
    }, []);

    const organizationCategoryActions: Array<ISelectionAction> = [
        {
            translateKey: 'ORGANIZATION_CATEGORY_STATE',
            enabled: true,
            visible: true,
            selectionValue: OrganizationCategoryAction.ORGANIZATION_CATEGORY_STATE,
            showValue: false,
            valueElements: [],
            actionValue: null,
            desc: 'CATEGORY_STATE_DESCRIPTION',
        },
        {
            translateKey: 'ORGANIZATION_CATEGORY_REGION',
            enabled: true,
            visible: true,
            selectionValue: OrganizationCategoryAction.ORGANIZATION_CATEGORY_REGION,
            showValue: false,
            valueElements: [],
            actionValue: null,
            desc: 'CATEGORY_REGIONS_DESCRIPTION',
        },
        {
            translateKey: 'ORGANIZATION_CATEGORY_MUNICIPALITY',
            enabled: true,
            visible: true,
            selectionValue: OrganizationCategoryAction.ORGANIZATION_CATEGORY_MUNICIPALITY,
            showValue: false,
            valueElements: [],
            actionValue: null,
            desc: 'CATEGORY_MUNICIPALITY_DESCRIPTION',
        },
        {
            translateKey: 'ORGANIZATION_CATEGORY_OTHER',
            enabled: true,
            visible: true,
            selectionValue: OrganizationCategoryAction.ORGANIZATION_CATEGORY_OTHER,
            showValue: false,
            valueElements: [],
            actionValue: null,
            desc: 'CATEGORY_OTHER_DESCRIPTION',
        },
    ];

    return (
        <Popup skipHeaderTranslation show headertext={formatMessage({ id: 'CHOOSE_RECIPIENT' })} onClosePopup={onClose}>
            <div>{formatMessage({ id: 'RECIEPIENT_SUB_HEADING_TEXT' })}</div>
            <PopupContent className="d-flex flex-column my-3 align-items-center">
                <div className="w-100">
                    <div className="d-flex flex-column w-100 mb-2 align-items-center" style={{ gap: '16px' }}>
                        {trusteeSelector}
                        {(selectedUserAccess.accessType === MailboxAccessType.OWNER ||
                            selectedUserAccess?.privileges.includes(MailboxAccessType.FULL_POWER_OF_ATTORNEY)) && (
                            <>
                                {isCategorySelected && (
                                    <div
                                        className={styles.selectedCategory}
                                        onClick={() => {
                                            setIsCategorySelected(false);
                                            setCategory('');
                                        }}
                                    >
                                        <Image src={images.arrowBackward} alt="arror backward" />
                                        {formatMessage({
                                            id: organizationCategoryActions.find(
                                                (item) => item.selectionValue === category
                                            ).translateKey,
                                        })}
                                    </div>
                                )}

                                <div className="w-100 mt-2 mb-2">
                                    <div
                                        className={['d-flex flex-fill justify-content-center', styles.searchInput].join(
                                            ' '
                                        )}
                                    >
                                        <div className="input-group w-100">
                                            <input
                                                value={inputValue}
                                                autoComplete="on"
                                                ref={inputElement}
                                                name="searchCategory"
                                                placeholder={formatMessage({ id: 'ORGANIZATION_CATEGORY_SEARCH_TEXT' })}
                                                aria-label={formatMessage({ id: 'ORGANIZATION_CATEGORY_TEXT' })}
                                                onChange={(e) => handleOnChange(e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                type="text"
                                                className="form-control"
                                            />
                                            {inputValue !== '' && (
                                                <button
                                                    type="button"
                                                    className="btn bg-transparent border-0"
                                                    style={{ marginLeft: '-30px', zIndex: '100' }}
                                                    onClick={() => {
                                                        setInputValue('');
                                                    }}
                                                >
                                                    <Image
                                                        src={images.close}
                                                        alt="CLOSE"
                                                        style={{ marginBottom: '4px' }}
                                                    />
                                                </button>
                                            )}
                                        </div>
                                        <div
                                            className={styles.searchButton}
                                            style={{ backgroundColor: config.colors.buttons.primary.background }}
                                        >
                                            <a
                                                className="d-flex align-items-center justify-content-center w-100 h-100"
                                                onClick={searchClick}
                                                href={staticConfig.defaultUrl}
                                            >
                                                <Image src={images.search} alt="SEARCH" />
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                {!isCategorySelected && searchValue == '' && (
                                    <div className={styles.categoryOuter}>
                                        {organizationCategoryActions.map((item) => (
                                            <div
                                                className={styles.categoryInner}
                                                key={item.selectionValue}
                                                onClick={() => handleItemSelected(item.selectionValue)}
                                            >
                                                <div>
                                                    <p className={`m-0 ${styles.categoryHeader}`}>
                                                        {formatMessage({ id: item.translateKey })}
                                                    </p>
                                                    <p className={`m-0 ${styles.categoryDescription}`}>
                                                        {formatMessage({ id: item.desc })}
                                                    </p>
                                                </div>
                                                <div className={styles.categoryIcon}>
                                                    <Image src={images.arrowForward} alt="arror forward" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {(isCategorySelected || searchValue != '') && (
                                    <div className={`${styles.searchArea} w-100 mb-2`}>
                                        <ScrollView className={styles.scrollSearch}>
                                            <div
                                                className={`${showEmptyText ? 'd-flex' : ''} ${styles.searchResult} ${
                                                    styles.contactPointViewList
                                                }`}
                                                data-cy="contactPointSearcher"
                                                role="tree"
                                            >
                                                {isLoading && <FixedLoader size={mediaSize} />}

                                                {!isLoading && viewElements}

                                                {showEmptyText && (
                                                    <div className="d-flex w-100 text-center justify-content-center align-items-center">
                                                        <FormattedMessage id="RECIPIENT_SEARCH_EMPTY_TEXT" />
                                                    </div>
                                                )}
                                            </div>
                                        </ScrollView>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </PopupContent>

            <PopupButtons>
                <div className="d-flex justify-content-end w-100 mt-3">
                    <Button type="button" variant="secondary" onClick={onClose}>
                        <FormattedMessage id="CANCEL" />
                    </Button>
                </div>
            </PopupButtons>
        </Popup>
    );
};
